import * as THREE from "three";

import type { ScorePopup, WorldSnapshot } from "../simulation/snapshot";
import { sampleHeight } from "../world/terrain";

const PROJECT = new THREE.Vector3();

type PopupSlot = {
  el: HTMLElement;
  labelEl: HTMLElement;
  pointsEl: HTMLElement;
  label: string;
  points: number;
};

/** Screen-space floating score callouts at world kill positions. */
export class ScorePopupLayer {
  private readonly layer: HTMLElement;
  private readonly slots: PopupSlot[] = [];
  private canvasW = 0;
  private canvasH = 0;
  private rectFrame = -1;
  private frame = 0;

  public constructor(host: HTMLElement) {
    this.layer = document.createElement("div");
    this.layer.className = "score-popups";
    this.layer.setAttribute("aria-hidden", "true");
    host.appendChild(this.layer);
  }

  public render(
    snap: WorldSnapshot,
    camera: THREE.PerspectiveCamera,
    canvas: HTMLCanvasElement,
  ): void {
    const popups = snap.scorePopups;
    if (popups.length === 0) {
      this.hideFrom(0);
      return;
    }

    this.frame += 1;
    if (this.canvasW === 0 || this.canvasH === 0 || this.frame - this.rectFrame >= 8) {
      const rect = canvas.getBoundingClientRect();
      this.canvasW = rect.width;
      this.canvasH = rect.height;
      this.rectFrame = this.frame;
    }
    const width = this.canvasW;
    const height = this.canvasH;
    if (width === 0 || height === 0) return;

    let used = 0;
    for (const popup of popups) {
      const pos = projectWorld(popup, snap.seed, camera, width, height);
      if (!pos) continue;
      const fade = popup.life / popup.maxLife;
      const rise = (1 - fade) * 28;
      this.place(used, popup, pos.x, pos.y - rise, fade);
      used += 1;
    }
    this.hideFrom(used);
  }

  public dispose(): void {
    this.layer.remove();
    this.slots.length = 0;
  }

  private place(index: number, popup: ScorePopup, x: number, y: number, fade: number): void {
    let slot = this.slots[index];
    if (!slot) {
      const el = document.createElement("span");
      el.className = "score-popup";
      const labelEl = document.createElement("em");
      const pointsEl = document.createElement("strong");
      el.append(labelEl, pointsEl);
      this.layer.appendChild(el);
      slot = { el, labelEl, pointsEl, label: "", points: -1 };
      this.slots[index] = slot;
    }
    slot.el.hidden = false;
    if (slot.label !== popup.label) {
      slot.labelEl.textContent = popup.label;
      slot.label = popup.label;
    }
    if (slot.points !== popup.points) {
      slot.pointsEl.textContent = popup.points > 0 ? `+${popup.points}` : "";
      slot.pointsEl.hidden = popup.points <= 0;
      slot.points = popup.points;
    }
    slot.el.style.left = `${x}px`;
    slot.el.style.top = `${y}px`;
    slot.el.style.opacity = String(fade);
    slot.el.style.transform = `translate(-50%,-50%) scale(${0.85 + fade * 0.2})`;
  }

  private hideFrom(start: number): void {
    for (let i = start; i < this.slots.length; i += 1) {
      this.slots[i]!.el.hidden = true;
    }
  }
}

function projectWorld(
  popup: ScorePopup,
  seed: number,
  camera: THREE.PerspectiveCamera,
  width: number,
  height: number,
): { x: number; y: number } | null {
  PROJECT.set(popup.x, sampleHeight(seed, popup.x, popup.y) + 4, popup.y).project(camera);
  if (PROJECT.z > 1) return null;
  return {
    x: ((PROJECT.x + 1) / 2) * width,
    y: ((1 - PROJECT.y) / 2) * height,
  };
}
